import React, { ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';
// eslint-disable-next-line import/no-namespace
import * as SS from 'styled-system';

import Box from 'components/Box';
import Hide from 'components/Hide';
import Icon from 'components/Icon/Icon';
import { Link as ReactRouterLink } from 'components/RouterLink';
import TrackedLink from 'components/TrackedLink';
import { colors, shadows } from 'theme';
import ClickOutside from 'utils/ClickOutside';

const NavLink = styled.a<{ blue?: boolean }>`
  font-size: inherit;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  &:disabled {
    opacity: 1/4;
  }
  ${SS.fontWeight};
  ${(props) =>
    props.blue
      ? `
    color: ${colors.waterBlue};
  `
      : `
    color: #717375;
  `}
`;
const RouterNavLink = NavLink.withComponent(ReactRouterLink);
const MenuList = styled.div<{ up?: boolean }>`
  ${(props) =>
    props.up &&
    css`
      display: flex;
      flex-direction: column-reverse;
    `};
`;
const StyledDropdownMenuNav = styled(NavLink)`
  display: flex;
  align-items: center;
  margin: 0;
  padding: 11px 12px;
  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;

type HideOptions = {
  xs?: boolean;
  sm?: boolean;
  md?: boolean;
  lg?: boolean;
  xl?: boolean;
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const DropdownMenuNav = ({
  open,
  up,
  children,
  hideArrow,
  ...props
}: {
  open: boolean;
  up?: boolean;
  children: ReactNode;
  hideArrow?: HideOptions;
}) => (
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/jsx-props-no-spreading
  <StyledDropdownMenuNav {...props}>
    <Box>{children}</Box>
    {/* TODO: Fix this the next time the file is edited. */}
    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
    <Hide {...hideArrow}>
      <Box mt="-2px" ml="5px">
        <Icon
          name="chevron"
          width={9}
          height={5}
          direction={(open && !up) || (!open && up) ? 'down' : 'up'}
          fill="currentColor"
        />
      </Box>
    </Hide>
  </StyledDropdownMenuNav>
);

export const DropdownMenuItem = styled(NavLink)<{ disabled?: boolean }>`
  display: block;
  margin: 0;
  max-width: 220px;
  min-width: 100%;
  padding: 10px 12px;
  overflow: hidden;
  cursor: pointer;
  color: ${colors.battleshipGrey};
  text-overflow: ellipsis;
  text-decoration: none;
  font-size: inherit;
  white-space: nowrap;
  font-weight: normal;

  &:hover {
    background-color: #f2eeee;
    text-decoration: none;
    color: ${colors.greyishBrown};
  }

  ${(props) =>
    props.disabled &&
    css`
      opacity: 1/4;
      color: #484848;
      cursor: not-allowed;
      background-color: #f2eeee;
      text-decoration: none;
    `};
`;
export const DropdownMenuItemRouter =
  DropdownMenuItem.withComponent(RouterNavLink);
export const TrackedDropdownMenuItem =
  DropdownMenuItem.withComponent(TrackedLink);
const DropdownMenuContainer = styled.div<{ up?: boolean; open?: boolean }>`
  position: absolute;
  left: 0;
  z-index: 2000;
  overflow: hidden;
  box-shadow: ${shadows.highest};
  background-color: white;
  border-radius: 8px;
  transition: all 0.166s;
  ${(props) =>
    props.open
      ? `
    opacity: 1;
    visibility: visible;
  `
      : `
    opacity: 0;
    visibility: hidden;
  `}
  ${(props) =>
    props.up
      ? css`
          bottom: 0;
          ${DropdownMenuItem}, ${DropdownMenuItemRouter} {
            border-bottom: solid 1px ${colors.aluminum};
          }
        `
      : css`
          top: 0;
          ${DropdownMenuItem}, ${DropdownMenuItemRouter} {
            border-top: solid 1px ${colors.aluminum};
          }
        `};
`;
const StyledDropdownMenu = styled.div<{ right?: boolean }>`
  position: relative;
  ${(props) =>
    props.right &&
    css`
      margin-left: auto;
    `};
`;
type Props = {
  title: (arg0: boolean) => ReactNode;
  right?: boolean;
  up?: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any;
  'data-qa-id': string;
  openQaId?: string;
  closeQaId?: string;
  dropdownQaId?: string;
  'aria-label'?: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (...args: Array<any>) => any;
  blue?: boolean;
  hideArrow?: HideOptions;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose?: (...args: Array<any>) => any;
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const DropdownMenu = ({
  title,
  right,
  up,
  children,
  'data-qa-id': qaId,
  openQaId,
  closeQaId,
  dropdownQaId,
  'aria-label': ariaLabel,
  onClick,
  blue,
  hideArrow,
  onClose,
  ...props
}: Props) => {
  const [open, setOpen] = useState(false);

  const toggleMenu = (ev: Event) => {
    if (open && onClose) {
      onClose();
    }

    setOpen(!open);
    ev.preventDefault();
  };

  const closeMenu = () => {
    if (open && onClose) {
      onClose();
    }

    setOpen(false);
  };

  const dropdownNavClick = (e) => {
    toggleMenu(e);

    if (onClick) {
      onClick(e, !open);
    }
  };

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/function-component-definition, react/no-unstable-nested-components
  const DropdownNav = ({ menuState }: { menuState: string }) => (
    <DropdownMenuNav
      // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: ReactNode; href: string; onClick... Remove this comment to see the full error message
      href="#"
      onClick={dropdownNavClick}
      open={open}
      up={up}
      data-qa-id={qaId ? `${qaId}-${menuState}` : undefined}
      aria-label={ariaLabel}
      blue={blue}
      hideArrow={hideArrow}
    >
      {title(open)}
    </DropdownMenuNav>
  );

  return (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <StyledDropdownMenu right={right} {...props}>
      <DropdownNav menuState="open" />
      <DropdownMenuContainer open={open} up={up} data-qa-id={qaId}>
        <ClickOutside onClickOutside={closeMenu}>
          <MenuList up={up}>
            <DropdownNav menuState="close" />
            {children({
              closeMenu,
              open,
            })}
          </MenuList>
        </ClickOutside>
      </DropdownMenuContainer>
    </StyledDropdownMenu>
  );
};

// eslint-disable-next-line import/no-default-export
export default DropdownMenu;
export const DropdownMenuSection = styled.div`
  border-top: solid 1px ${colors.aluminum};
  width: 100%;
  padding: 9px 0;
`;
export const DropdownMenuSectionTitle = styled.div`
  color: #b0b4b9;
  font-size: inherit;
  font-style: italic;
  text-decoration: none;
  white-space: nowrap;
  display: block;
  margin: 0;
  padding: 6px 12px;
`;
export const DropdownMenuSectionItem = styled(DropdownMenuItem)`
  border: none;
  padding: 6px 12px;
`;
export const DropdownMenuSectionItemRouter = styled(DropdownMenuItemRouter)`
  border: none;
  padding: 6px 12px;
`;
